import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ReviewKind } from './types';
import { KIND_META } from './constants';
import { useCleanupReview } from '../../../../hooks/useCleanupReview';
import HeaderActions from './HeaderActions';
import VelaBanner from './VelaBanner';
import EmptyBody from './EmptyBody';
import LoadingBody from './LoadingBody';
import ErrorBody from './ErrorBody';
import ArchiveBody from './archive/ArchiveBody';
import MergeBody from './merge/MergeBody';
import CategorizeBody from './categorize/CategorizeBody';

function kindFromPath(pathname: string): ReviewKind {
  if (pathname.endsWith('/review-merge')) return 'merge';
  if (pathname.endsWith('/review-categorize')) return 'categorize';
  return 'archive';
}

interface HeaderProps {
  kind?: ReviewKind;
}

export function ReviewPageHeader({ kind: kindProp }: HeaderProps = {}): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const kind = kindProp ?? kindFromPath(location.pathname);
  const meta = KIND_META[kind];

  return (
    <div className="px-4 py-3.5 md:px-8">
      <button
        type="button"
        onClick={() => navigate('/accounting/chart-of-accounts')}
        className="inline-flex items-center gap-0.5 text-teal-dark text-small font-semibold bg-transparent border-none p-0 cursor-pointer hover:underline mb-2"
      >
        <span className="text-[15px] leading-none mr-0.5">&lsaquo;</span> Back to Chart of accounts
      </button>
      <div className="text-small text-helper font-medium mb-1">
        <span className="cursor-pointer hover:text-ink" onClick={() => navigate('/accounting')}>Accounting</span>
        <span className="mx-1.5 text-ink4">/</span>
        <span className="cursor-pointer hover:text-ink" onClick={() => navigate('/accounting/chart-of-accounts')}>Chart of accounts</span>
        <span className="mx-1.5 text-ink4">/</span>
        <span>{meta.breadcrumb}</span>
      </div>
      <div className="flex justify-between items-end gap-5">
        <h1 className="text-hero font-extrabold text-ink m-0 leading-[1.1]">
          {meta.title}
        </h1>
        <HeaderActions kind={kind} />
      </div>
    </div>
  );
}

export default function CleanupReviewPage(): React.ReactElement {
  const location = useLocation();
  const kind = kindFromPath(location.pathname);
  const { state, data, count, refetch } = useCleanupReview(kind);

  return (
    <div className="max-w-full m-0 px-8 pb-[90px]">
      {state === 'populated' && <VelaBanner kind={kind} count={count} />}
      <div className="mt-5">
        {state === 'populated' && kind === 'archive' && data && data.kind === 'archive' && (
          <ArchiveBody items={data.items} />
        )}
        {state === 'populated' && kind === 'merge' && data && data.kind === 'merge' && (
          <MergeBody pairs={data.pairs} />
        )}
        {state === 'populated' && kind === 'categorize' && data && data.kind === 'categorize' && (
          <CategorizeBody items={data.items} />
        )}
        {state === 'empty' && <EmptyBody kind={kind} />}
        {state === 'loading' && <LoadingBody />}
        {state === 'error' && <ErrorBody kind={kind} onRetry={refetch} />}
      </div>
    </div>
  );
}